import { PopUpMain } from "../../component/popUp";
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  IconButton,
  Typography,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import * as yup from "yup";
import TextField from "@mui/material/TextField";
import {
  ErrorMessage,
  Field,
  FieldArray,
  FieldProps,
  Form,
  Formik,
} from "formik";
import * as React from "react";
import Divider from "@mui/material/Divider";
import { useDispatch, useSelector } from "react-redux";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { AppDispatch, RootState } from "../../store/store";
import {
  addSteelOnOrder,
  editSteelOnOrder,
  getSteelOnOrderList,
} from "../../api/steelonorder/Api.service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { deleteAndShift } from "../../utils/deleteAndShift";
import titleCase from "../../utils/titleCase";
import { reformatDates } from "../../utils/reformatDates";
import { clearSteelData } from "../steel/slice/SteelSlice";
import { getSteelList } from "../../api/steel/Api.service";
import { clearListData } from "./slice/SteelOnOrderSlice";

const errorStyles = {
  marginLeft: "0px",
  marginRight: "0px",
};

export const AddShipment = (props: any): JSX.Element => {
  const { title = "Add Steel" } = props;

  return (
    <Box>
      <PopUpMain title={title} {...props} size={false}>
        <Box>
          <AddShipmentScreen {...props} />
        </Box>
      </PopUpMain>
    </Box>
  );
};

export const AddShipmentScreen = (props: any): JSX.Element => {
  const emptySteelObject = {
    lineId: "",
    steelId: "",
    code: "",
    brand: "",
    shipmentItemId: "",
    receivedQty: "",
  };

  const { handleClose, editData, refreshSteelOnOrder } = props;
  const { filterData, steelOnOrderListData } = useSelector(
    (state: RootState) => state.steelOnOrderState
  );
  const { steelListData } = useSelector((state: any) => state.steelState);

  const dispatch: AppDispatch = useDispatch();
  const { userInfo } = useSelector((state: RootState) => state.globalAppState);

  const [code, setCode] = React.useState({});

  const handleOnDateChange = (
    event: DateTime | null,
    fieldName: any,
    Name: any,
    index: number
  ) => {
    if (
      Name.includes("purchaseDate") ||
      Name.includes("forecastedDate") ||
      Name.includes("deliveryDate")
    ) {
      if (event) {
        const tempDate = event.toISODate();
        fieldName(Name, tempDate);
      } else {
        fieldName(Name, "");
      }
    }
  };

  const handleChange = (
    event: any,
    fieldName: any,
    Name: any,
    index: any,
    values?: any,
    setValues?: any
  ) => {
    let {
      target: { value },
    } = event;

    if (Name.includes("steelId")) {
      // empty steel and brand on code change
      fieldName(`items[${index}].code`, "");
      fieldName(`items[${index}].brand`, "");
      fieldName(`items[${index}].quantity`, "");
      fetchNewSteels(value, index);
    }

    if (Name.includes("locationId")) {
      fieldName(`orderNumber`, "");
      fieldName(`supplier`, "");
      values.items.forEach((value: any, index: number) => {
        fieldName(`items[${index}]["lineId"]`, "");
        fieldName(`items[${index}]["steelId"]`, "");
        fieldName(`items[${index}].["code"]`, "");
        fieldName(`items[${index}].["brand"]`, "");
      });

      dispatch(clearSteelData());
      dispatch(
        getSteelList({
          page: 1,
          pageSize: 1000,
          location: value,
        })
      );
      dispatch(clearListData());
      dispatch(
        getSteelOnOrderList({
          page: 1,
          pageSize: 1000,
          location: value,
          isPurchaseView: true,
        })
      );
    }

    if (Name.includes("orderNumber")) {
      values.items = [];

      const steelorderitems = steelOnOrderListData.rows.filter(
        (item: any) => item._id === value
      )[0].steelorderitems;

      steelorderitems.forEach((item: any, index: number) => {
        const { lineId, steelId, code, brand } = item;
        fetchNewSteels(steelId, index);
        if (index < steelorderitems.length) {
          values.items.push({
            lineId,
            steelId,
            code,
            brand,
            receivedQty: "",
          });
        }
      });
    }
    if (Name) fieldName(Name, value);
  };

  const fetchNewSteels = (value: any, index: number) => {
    const resp = steelListData?.rows?.filter(
      (steel: any) => steel.id === value
    );
    const tempKey = `index-${index}`;
    setCode((prevCode) => ({
      ...prevCode,
      [tempKey]: resp,
    }));
  };

  const handleCallBack = () => {
    refreshSteelOnOrder();
    handleClose();
  };

  React.useEffect(() => {
    if (!editData?._id) {
      dispatch(clearListData());
      if (
        userInfo?.jobRole?.key === "supply_chain_manager" ||
        userInfo?.jobRole?.key === "location_admin"
      ) {
        dispatch(
          getSteelOnOrderList({
            page: 1,
            pageSize: 1000,
            location: userInfo?.ownLocation?.locationId,
            isPurchaseView: true,
          })
        );
      }
    }
    dispatch(
      getSteelList({
        page: 1,
        pageSize: 1000,
        location: editData?.locationId || userInfo?.ownLocation?.locationId,
      })
    );
  }, []);

  React.useEffect(() => {
    if (editData?._id) {
      const editSteel = editData?.items?.reduce(
        (acc: any, steel: any, index: number) => {
          const response = steelListData?.rows?.filter(
            (steelData: any) => steelData._id === steel.steelId
          );
          const tempKey = `index-${index}`;
          acc[tempKey] = response;
          return acc;
        },
        {}
      );
      setCode(editSteel);
    }
  }, [editData, steelListData]);

  const validationSchema = yup.object({
    name: yup.string().test("len", "Must be less than 30 characters", (val) => {
      if (val === undefined) return true;
      return val.length === 0 || (val.length >= 1 && val.length <= 30);
    }),
    items: yup.array().of(
      yup.object().shape({
        receivedQty: yup
          .number()
          .min(1, "Qty must be greater than 1")
          .max(999.99, "Qty is exceeding the limit"),
      })
    ),
  });

  const initialSteelOnOrderData = {
    steelOrderId: "",
    shipmentId: "",
    name: "",
    shipmentType: "",
    deliveryDate: null,
    locationId:
      userInfo?.jobRole?.key === "supply_chain_manager" ||
      userInfo?.jobRole?.key === "location_admin"
        ? userInfo?.ownLocation?.locationId
        : "",
    items: [
      {
        shipmentItemId: "",
        lineId: "",
        steelId: "",
        code: "",
        brand: "",
        receivedQty: "",
      },
    ],
  };

  return (
    <React.Fragment>
      <>
        {false ? (
          <>
            <Box
              ml={0}
              sx={{
                p: 3,
                backgroundColor: "#fff ",
                height: "100%",
                ml: 0,
              }}
            >
              <Stack spacing={3}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    gap: "30px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h5" align="left">
                    Asset type
                  </Typography>
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "5vh", width: "50%" }}
                    animation={"wave"}
                  />
                </Grid>
                <Divider />
                <Grid
                  container
                  spacing={2}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography variant="subtitle1" align="left">
                    Add steel and quantity for each asset:
                  </Typography>
                </Grid>
                <Grid
                  sx={{
                    gap: "30px",
                    display: "flex",
                    marginTop: "20px",
                    marginLeft: "20px",
                  }}
                  spacing={2}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                </Grid>

                <Grid
                  sx={{
                    gap: "30px",
                    display: "flex",
                    marginTop: "20px",
                    marginLeft: "20px",
                  }}
                  spacing={2}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                </Grid>

                <Grid
                  sx={{
                    gap: "30px",
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px",
                    marginLeft: "20px",
                  }}
                  spacing={2}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                  <Skeleton
                    variant="rounded"
                    sx={{ height: "6vh", width: "150px" }}
                  />
                </Grid>
              </Stack>
            </Box>
          </>
        ) : (
          <>
            <Formik
              initialValues={
                editData?._id
                  ? editData
                    ? editData
                    : initialSteelOnOrderData
                  : initialSteelOnOrderData
              }
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                const addData = {
                  isPurchaseView: false,
                  callback: handleCallBack,
                  data: {
                    steelOrderId: values?.orderNumber,
                    locationId: values?.locationId,
                    deliveryDate: reformatDates(values?.deliveryDate),
                    name: values?.name,
                    shipmentType: values?.shipmentType,
                  },
                };
                if (values?.items[0]?.lineId) {
                  // @ts-ignore
                  addData.data.items = values?.items?.map((item: any) => ({
                    lineId: item?.lineId,
                    steelId: item?.steelId,
                    receivedQty: item?.receivedQty,
                  }));
                }

                if (editData?._id) {
                  const updateData = {
                    callback: handleCallBack,
                    isPurchaseView: false,
                    data: {
                      ...addData.data,
                      shipmentId: editData?._id,
                    },
                  };
                  // @ts-ignore
                  updateData.data.items = updateData?.data?.items?.map(
                    (item: any, index: number) => ({
                      ...item,
                      shipmentItemId: editData?.items[index]?._id,
                    })
                  );

                  dispatch(editSteelOnOrder(updateData));
                } else {
                  dispatch(addSteelOnOrder(addData));
                }
              }}
            >
              {({
                values,
                resetForm,
                isSubmitting,
                errors,
                setFieldValue,
                setValues,
              }) => (
                <>
                  <Box
                    ml={0}
                    sx={{
                      p: 3,
                      backgroundColor: "#fff ",
                    }}
                  >
                    <Form>
                      <Grid container spacing={2}>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Location
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`locationId`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <TextField
                                    select
                                    label="Location"
                                    name={`locationId`}
                                    id={`locationId`}
                                    type="text"
                                    required
                                    style={{
                                      width: "200px",
                                    }}
                                    variant="outlined"
                                    size="small"
                                    value={field.value ?? ""}
                                    disabled={
                                      editData?._id ||
                                      userInfo?.jobRole?.key ===
                                        "supply_chain_manager" ||
                                      userInfo?.jobRole?.key ===
                                        "location_admin"
                                    }
                                    fullWidth
                                    onChange={(event: any) =>
                                      handleChange(
                                        event,
                                        setFieldValue,
                                        `locationId`,
                                        0,
                                        values
                                      )
                                    }
                                  >
                                    {filterData?.allowedLocations?.map(
                                      (option: any, i: number) => (
                                        // @ts-ignore
                                        <MenuItem
                                          key={option?._id}
                                          value={option?._id}
                                        >
                                          {titleCase(option?.fullName)}
                                        </MenuItem>
                                      )
                                    )}
                                  </TextField>
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Shipment Name
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`name`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <TextField
                                    label="Shipment Name"
                                    name={`name`}
                                    id={`name`}
                                    type="text"
                                    variant="outlined"
                                    required
                                    style={{
                                      width: "200px",
                                    }}
                                    fullWidth
                                    size="small"
                                    value={field.value ?? ""}
                                    onChange={(event: any) => {
                                      handleChange(
                                        event,
                                        setFieldValue,
                                        `name`,
                                        undefined
                                      );
                                    }}
                                  />
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Purchase Order No.
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`orderNumber`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <TextField
                                    select
                                    required
                                    label="Purchase Order No."
                                    name={`orderNumber`}
                                    id={`orderNumber`}
                                    type="text"
                                    style={{
                                      width: "200px",
                                    }}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    disabled={
                                      editData?._id || !values?.locationId
                                    }
                                    // value={values?.orderNumber}
                                    value={field.value ?? ""}
                                    onChange={(event: any) => {
                                      handleChange(
                                        event,
                                        setFieldValue,
                                        `orderNumber`,
                                        undefined,
                                        values,
                                        setValues
                                      );
                                    }}
                                  >
                                    {steelOnOrderListData?.rows?.map(
                                      (option: any, i: number) => (
                                        // @ts-ignore
                                        <MenuItem
                                          key={option?._id}
                                          value={option?._id}
                                        >
                                          {option?.purchaseOrder}
                                        </MenuItem>
                                      )
                                    )}
                                  </TextField>
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Shipment Type
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`shipmentType`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <TextField
                                    select
                                    label="Shipment Type"
                                    name={`shipmentType`}
                                    id={`shipmentType`}
                                    type="text"
                                    required
                                    style={{
                                      width: "200px",
                                    }}
                                    variant="outlined"
                                    size="small"
                                    value={field.value ?? ""}
                                    disabled={editData?._id}
                                    fullWidth
                                    onChange={(event: any) =>
                                      handleChange(
                                        event,
                                        setFieldValue,
                                        `shipmentType`,
                                        0,
                                        values
                                      )
                                    }
                                  >
                                    {filterData?.shipmentType?.map(
                                      (option: any, i: number) => (
                                        // @ts-ignore
                                        <MenuItem key={option} value={option}>
                                          {titleCase(option)}
                                        </MenuItem>
                                      )
                                    )}
                                  </TextField>
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Supplier
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`supplier`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <TextField
                                    label="Supplier"
                                    name={`supplier`}
                                    id={`supplier`}
                                    style={{
                                      width: "200px",
                                    }}
                                    type="text"
                                    variant="outlined"
                                    required
                                    fullWidth
                                    size="small"
                                    disabled
                                    value={
                                      steelOnOrderListData?.rows?.filter(
                                        (item: any) =>
                                          item._id === values.orderNumber
                                      )[0]?.supplier ?? ""
                                    }
                                    onChange={(event: any) => {
                                      handleChange(
                                        event,
                                        setFieldValue,
                                        `supplier`,
                                        undefined
                                      );
                                    }}
                                  />
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                        <Grid item xs={3} sm={3} md={3}>
                          <Typography sx={{ marginTop: "10px" }} align="right">
                            Delivery Date
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          pb={2}
                        >
                          <Field name={`deliveryDate`}>
                            {({ field, meta }: FieldProps) => {
                              return (
                                <FormControl>
                                  <LocalizationProvider
                                    dateAdapter={AdapterLuxon}
                                  >
                                    <DatePicker
                                      label="Delivery Date"
                                      format="dd/MM/yyyy"
                                      onChange={(event) =>
                                        handleOnDateChange(
                                          event,
                                          setFieldValue,
                                          `deliveryDate`,
                                          0
                                        )
                                      }
                                      value={
                                        values?.deliveryDate
                                          ? DateTime.fromJSDate(
                                              new Date(values?.deliveryDate)
                                            )
                                          : null
                                      }
                                      minDate={
                                        steelOnOrderListData?.rows?.filter(
                                          (item: any) =>
                                            item._id === values.orderNumber
                                        )[0] &&
                                        DateTime.fromJSDate(
                                          new Date(
                                            steelOnOrderListData?.rows?.filter(
                                              (item: any) =>
                                                item._id === values.orderNumber
                                            )[0].purchaseDate
                                          )
                                        )
                                      }
                                      slotProps={{
                                        textField: {
                                          size: "small",
                                          fullWidth: true,
                                          id: `deliveryDate`,
                                          name: `deliveryDate`,
                                          required: true,
                                          sx: {
                                            width: "200px",
                                          },
                                        },
                                        actionBar: {
                                          actions: ["clear"],
                                        },
                                      }}
                                    />
                                  </LocalizationProvider>
                                  <FormHelperText
                                    error={!!meta.error}
                                    style={errorStyles}
                                  >
                                    {meta.error}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          </Field>
                        </Grid>
                      </Grid>
                      <Grid>
                        <FieldArray name="items">
                          {({ push, remove }) => (
                            <React.Fragment>
                              {values?.items?.map((_: any, index: any) => (
                                <div className="add-more-container-a">
                                  <Grid container item key={index} spacing={2}>
                                    <Grid item xs={2.25} sm={2.25} md={2.25}>
                                      <Field name={`items[${index}].lineId`}>
                                        {({ field, meta }: FieldProps) => (
                                          <FormControl>
                                            <TextField
                                              label="Line ID"
                                              name={`items[${index}].lineId`}
                                              id={`items[${index}].lineId`}
                                              type="number"
                                              required
                                              disabled
                                              size="small"
                                              value={field.value ?? ""}
                                              onChange={(event: any) => {
                                                handleChange(
                                                  event,
                                                  setFieldValue,
                                                  `items[${index}].lineId`,
                                                  index
                                                );
                                              }}
                                            />
                                            <FormHelperText
                                              error={!!meta.error}
                                              style={errorStyles}
                                            >
                                              {meta.error}
                                            </FormHelperText>
                                          </FormControl>
                                        )}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={2.25} sm={2.25} md={2.25}>
                                      <Field
                                        component={TextField}
                                        select
                                        type="text"
                                        value={values?.items[index]?.steelId}
                                        label="Product Name"
                                        // required={
                                        //   values?.items[index].lineId ||
                                        //   values?.items[index].steelId ||
                                        //   values?.items[index].totalOrderedQty
                                        // }
                                        required
                                        placeholder="Product Name"
                                        className="custom-select"
                                        disabled
                                        isMulti={true}
                                        size="small"
                                        fullWidth
                                        onChange={(event: any) =>
                                          handleChange(
                                            event,
                                            setFieldValue,
                                            `items[${index}].steelId`,
                                            index
                                          )
                                        }
                                      >
                                        {steelListData?.rows?.map(
                                          (option: any) => (
                                            <MenuItem
                                              key={option.id}
                                              value={option.id}
                                            >
                                              {option?.name}
                                            </MenuItem>
                                          )
                                        )}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={2.25} sm={2.25} md={2.25}>
                                      <Field
                                        component={TextField}
                                        select
                                        type="text"
                                        value={values?.items[index]?.code}
                                        placeholder="Code"
                                        label="Code"
                                        disabled
                                        className="custom-select"
                                        isMulti={true}
                                        // required={
                                        //   values?.items[index].lineId ||
                                        //   values?.items[index].steelId ||
                                        //   values?.items[index].totalOrderedQty
                                        // }
                                        required
                                        size="small"
                                        fullWidth
                                        onChange={(event: any) =>
                                          handleChange(
                                            event,
                                            setFieldValue,
                                            `items[${index}].code`,
                                            index
                                          )
                                        }
                                      >
                                        {Object.keys(code).length ? (
                                          // @ts-ignore
                                          code[`index-${index}`]?.map(
                                            (option: any) => {
                                              return (
                                                <MenuItem
                                                  key={option?.id}
                                                  value={option?.code}
                                                >
                                                  {option?.code}
                                                </MenuItem>
                                              );
                                            }
                                          )
                                        ) : (
                                          <MenuItem key={"random"} value={""}>
                                            {""}
                                          </MenuItem>
                                        )}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={2.25} sm={2.25} md={2.25}>
                                      <Field
                                        component={TextField}
                                        select
                                        type="text"
                                        // name={`steelUsed[${index}].brand`}
                                        // id={`steelUsed[${index}].brand`}
                                        value={values?.items[index]?.brand}
                                        placeholder="Brand"
                                        disabled
                                        required
                                        // required={
                                        //   values?.items[index].lineId ||
                                        //   values?.items[index].steelId ||
                                        //   values?.items[index].totalOrderedQty
                                        // }
                                        label="Brand"
                                        className="custom-select"
                                        isMulti={true}
                                        size="small"
                                        fullWidth
                                        onChange={(event: any) =>
                                          handleChange(
                                            event,
                                            setFieldValue,
                                            `items[${index}].brand`,
                                            index
                                          )
                                        }
                                      >
                                        {Object.keys(code).length ? (
                                          // @ts-ignore
                                          code[`index-${index}`]?.map(
                                            (option: any) => {
                                              return (
                                                <MenuItem
                                                  key={option?.id}
                                                  value={option?.brand}
                                                >
                                                  {option?.brand}
                                                </MenuItem>
                                              );
                                            }
                                          )
                                        ) : (
                                          <MenuItem key={"random"} value={""}>
                                            {""}
                                          </MenuItem>
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        className="error-class-message"
                                        component="div"
                                        name={`items[${index}].brand`}
                                      />
                                    </Grid>
                                    <Grid item xs={2.25} sm={2.25} md={2.25}>
                                      <Field
                                        name={`items[${index}].receivedQty`}
                                      >
                                        {({ field, meta }: FieldProps) => (
                                          <FormControl>
                                            <TextField
                                              label="Received Qty"
                                              name={`items[${index}].receivedQty`}
                                              id={`items[${index}].receivedQty`}
                                              type="number"
                                              size="small"
                                              required
                                              value={field.value ?? ""}
                                              onChange={(event: any) => {
                                                handleChange(
                                                  event,
                                                  setFieldValue,
                                                  `items[${index}].receivedQty`,
                                                  index
                                                );
                                              }}
                                            />
                                            <FormHelperText
                                              error={!!meta.error}
                                              style={errorStyles}
                                            >
                                              {meta.error}
                                            </FormHelperText>
                                          </FormControl>
                                        )}
                                      </Field>
                                    </Grid>
                                    {/* <Grid item xs={0.75} sm={0.75} md={0.75}>
                                      <div className="remove-add-more-item">
                                        {!editData?._id &&
                                        values?.items.length > 1 ? (
                                          <IconButton
                                            onClick={() => {
                                              remove(index);
                                              deleteAndShift(code, index);
                                            }}
                                          >
                                            <RemoveCircleIcon />
                                          </IconButton>
                                        ) : (
                                          <></>
                                        )}
                                      </div>
                                    </Grid> */}
                                  </Grid>
                                </div>
                              ))}

                              <Grid item>
                                {typeof errors.items === "string" ? (
                                  <Typography color="error">
                                    {errors.items}
                                  </Typography>
                                ) : null}
                              </Grid>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "end",
                                  marginBottom: "20px",
                                  marginTop: "20px",
                                }}
                              >
                                {/* <IconButton
                                  disabled={isSubmitting}
                                  onClick={() => push(emptySteelObject)}
                                >
                                  <AddCircleIcon />
                                </IconButton> */}
                              </div>
                            </React.Fragment>
                          )}
                        </FieldArray>
                      </Grid>
                      <Box className="popupBtn" display={"flex"} gap={2} pt={2}>
                        <Button
                          variant="contained"
                          disabled={isSubmitting}
                          type="submit"
                        >
                          {"Save"}
                        </Button>
                        <Button variant="outlined" onClick={handleClose}>
                          Cancel
                        </Button>
                        {editData?._id ? (
                          <></>
                        ) : (
                          <Button
                            variant="outlined"
                            type="reset"
                            onClick={(e) => {
                              resetForm();
                            }}
                            color="error"
                          >
                            Reset
                          </Button>
                        )}
                      </Box>
                    </Form>
                  </Box>
                </>
              )}
            </Formik>
          </>
        )}
      </>
    </React.Fragment>
  );
};
